import { Injectable, signal } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { FieldContext } from '../models/field-context';

/**
 * The field context from fieldData$, but only once it has loaded for the field the dashboard shows.
 * Null while it loads, after a failed call, and while the service still holds the previous field's
 * context (it outlives the page). The link editor starts from this context and saves the full tap
 * list, so anything else would unlink this field's taps or move another field's taps onto it.
 */
export function loadedFieldContext(data: any): FieldContext | null {
  const context = data?.fieldContext as FieldContext | null | undefined;
  if (!context || data.fieldContextLoading || data.fieldContextError || data.fieldId == null
      || String(context.fieldId) !== String(data.fieldId)) {
    return null;
  }
  return context;
}

@Injectable({
  providedIn: 'root'
})
export class FieldDashboardDataService {
  private fieldDataSubject = new BehaviorSubject<any>({
    fieldName: '',
    numberOfFields: 0,
    totalHectares: 0,
    country: '',
    fieldId: '',
    farmId: '',
    farmName: '',
    tenantId: '',
    boundaryPoints: [],
    startDate: null,
    endDate: null
  });

  fieldData$ = this.fieldDataSubject.asObservable();

  /** Fired after the field's links change, so the dashboard reloads the context it owns. */
  private fieldContextReloadSubject = new Subject<void>();
  fieldContextReload$ = this.fieldContextReloadSubject.asObservable();

  requestFieldContextReload(): void {
    this.fieldContextReloadSubject.next();
  }

  /** Asks Field Analysis to open its link editor on a tab, e.g. from the integration drawer. */
  private linkEditorRequestSubject = new Subject<'fms' | 'igator'>();
  linkEditorRequest$ = this.linkEditorRequestSubject.asObservable();

  requestLinkEditor(tab: 'fms' | 'igator'): void {
    this.linkEditorRequestSubject.next(tab);
  }

  /**
   * True while Field Analysis shows the link editor. The editor's modal has no Escape handling, so the
   * drawer leaves Escape alone while it is open rather than closing behind it.
   */
  readonly linkEditorOpen = signal(false);

  updateFieldData(data: any): void {
    const currentData = this.fieldDataSubject.value;
    const updatedData = { ...currentData, ...data };

    if (data.fieldId && data.fieldId !== currentData.fieldId) {
      updatedData.boundaryPoints = [];
    }
    
    this.fieldDataSubject.next(updatedData);
  }

  getFieldData(): any {
    return this.fieldDataSubject.value;
  }
}
